/**
 * Same-module-resolvable state accessors for the fact-row editor.
 *
 * Background — why this file exists at all (2026-05-26):
 *
 * The llui v0.5.7 compile-time path analyzer (`@llui/compiler`'s
 * `collect-deps.js:detectOpaqueStateFlow`) only tracks state reads
 * through a narrow set of shapes:
 *
 *   - Direct property access: `s.x`, `s.x.y`
 *   - Element access with literal key: `s['x']`, `s[0]`
 *   - arg0 of `helper(s)` where `helper` is a **bare Identifier**
 *     that resolves (cross-file or same-module) to a function
 *     declaration or const-arrow.
 *
 * Everything else — including arg0 of `host.method(s)` where the
 * callee is a `PropertyAccessExpression` — is treated as opaque. No
 * prefix is emitted into the component's `__prefixes` table; at
 * runtime the commit-cycle's dirty mask stays 0 when those paths
 * mutate; Phase 2 never re-evaluates the bindings; the DOM stays
 * stale. The Playwright spec at
 * `apps/client/e2e/09-create-edit-navigate.spec.ts §9.4` exhibits
 * this end-to-end: a fact-row's `data-dirty` attribute and the save
 * button's `disabled` prop never reflect a keystroke even though
 * `model.factDirty[key]` is correctly updated by the reducer.
 *
 * Fix shape: the editor used to read state through a
 * `FactRowHost.<method>(s, …)` indirection — opaque to the
 * analyzer. The reactive readers now route through this file's
 * bare-Identifier function declarations instead. The compiler's
 * delegation visitor follows `factDirtyAtKey(s, key)` cross-file,
 * walks the function body, and registers `s.factDirty` as a
 * tracked path. Msg constructors stay on `FactRowHost` — those are
 * event-handler callbacks, never on the reactive path.
 *
 * The state-shape interface below is intentionally a structural
 * subset of `Model` (not an import) so the editor's `Model extends
 * FactRowStateShape` constraint stays narrow and doesn't drag the
 * whole `apps/client/src/app.ts` Model surface into the editor.
 */
import type { EntityState, FactState } from '@dl/shared';

/**
 * Compose `errorKey(entityId, predicate)` — the same scheme `app.ts`
 * uses to index `factDirty`, `factSavedAt`, `inlineErrors`, etc. Kept
 * here so the editor + the per-fact accessors agree on the key shape
 * without an import cycle through `app.ts`. (Trivial enough that
 * forking the implementation is preferable to extracting it into yet
 * another shared module.)
 */
export function factRowKey(entityId: string, predicate: string): string {
  return `${entityId}/${predicate}`;
}

/**
 * Structural subset of `Model` required by the fact-row editor's
 * reactive state reads. Consumers' `Model` is constrained to extend
 * this shape, so the type system enforces alignment without an
 * import cycle.
 */
export interface FactRowStateShape {
  entities: Record<string, EntityState>;
  factDirty: Record<string, true>;
  factSavedAt: Record<string, number>;
  factSaving: Record<string, true>;
  factDirtyEpoch: Record<string, number>;
  inlineErrors: Record<string, string>;
  focusedFactKey: string | null;
  factHintDismissed: boolean;
  /**
   * The host's active calendar id. Surfaced on the shape so the
   * validity-row's reactive accessors can resolve the calendar
   * through a bare-Identifier accessor (`pickActiveCalendarId`) —
   * `host.activeCalendar(s)` was the documented opaque-state-flow
   * anti-pattern.
   */
  activeCalendarId: string;
  /**
   * Controlled-input dirty-buffer (per 2026-05-27 maintainer
   * thread). When set for `key`, the editor's reactive `value:`
   * accessor returns this string instead of the persisted fact
   * value, so the DOM faithfully reflects what the user typed even
   * across commits, async ACKs, and entity re-folds. Cleared on
   * successful save (epoch-matched ACK).
   */
  inputBuffer: Record<string, string>;
  /**
   * LLM author mode (Phase B). `aiDraftPending[key]` is set while a ✨
   * suggest-prose request is in flight (disables the button); `aiDraftError`
   * carries the last failure for the row. Keyed by `factRowKey`.
   */
  aiDraftPending: Record<string, true>;
  aiDraftError: Record<string, string>;
  /**
   * Phase D — resolved signed content URLs for asset ids, keyed by the
   * opaque `asset_id` an asset-kind fact stores. Lets the asset row render
   * the image inline (and the draft inbox a thumbnail) instead of the raw
   * id. Populated lazily by the `resolve-asset-urls` effect.
   */
  assetUrls: Record<string, string>;
  /**
   * The single open per-fact History dialog, or null when none is open.
   *
   * History was an inline `<details>` disclosure under the row. It pushed the
   * row's meta line down a level and made the fact row three stacked blocks
   * (history / reveal chip / validity) where the mock has one. Only one
   * history is ever inspected at a time, so it is one app-wide slice keyed by
   * the (entity, predicate) pair rather than per-row disclosure state.
   */
  historyOpen: { entityId: string; predicate: string } | null;
  /**
   * The trait the GM is currently pointing at in the trait strip, or null.
   *
   * A trait chip and the fact rows it owns are far apart on the Facts tab, and
   * nothing previously connected them: with five traits attached it was
   * guesswork which rows a chip controlled. Hovering or focusing a chip
   * highlights its rows. Purely presentational and ephemeral — never persisted,
   * never an op.
   */
  highlightedTrait: string | null;
}

/**
 * Resolve an entity by id. Returns `undefined` while the entity is
 * still loading or after a soft-delete fold dropped it.
 *
 * NOTE: declared as a `function` (not `const arrow`) on purpose —
 * the llui compiler's cross-file walker resolves function
 * declarations directly; arrow consts may not.
 */
export function entityAtId<S extends FactRowStateShape>(
  s: S,
  entityId: string,
): EntityState | undefined {
  return s.entities[entityId];
}

/**
 * Resolve the trait id list for an entity — empty array when the
 * entity isn't loaded. Bare-Identifier function declaration so the
 * cross-file walker resolves reactive accessors that delegate here.
 */
/** Bare-Identifier accessor — the host's active calendar id. */
export function pickActiveCalendarId<S extends FactRowStateShape>(s: S): string {
  return s.activeCalendarId;
}

export function traitsAtId<S extends FactRowStateShape>(
  s: S,
  entityId: string,
): readonly string[] {
  return s.entities[entityId]?.traits ?? [];
}

/**
 * Resolve a fact-state by `(entity, predicate)`. `undefined` when
 * the entity hasn't loaded yet or the predicate has no fact.
 */
export function factAtPredicate<S extends FactRowStateShape>(
  s: S,
  entityId: string,
  predicate: string,
): FactState | undefined {
  return s.entities[entityId]?.facts[predicate];
}

/**
 * Read the latest author for a fact, normalised to the design-system
 * provenance taxonomy ('gm' / 'llm' / 'player' / 'system'). The fold
 * stores authors as lower-case strings ('GM' → 'gm') via
 * `authorLabel()` in `@dl/shared/entities-fold`; we map them here.
 *
 * Returns `'gm'` as the default both for "no fact yet" and for any
 * unrecognized author string — the GM-default is the safe display
 * (no provenance badge will render in the GM case since it's the
 * default; see `viewFactChips`).
 */
export function authorAtPredicate<S extends FactRowStateShape>(
  s: S,
  entityId: string,
  predicate: string,
): 'gm' | 'llm' | 'player' | 'system' {
  const fact = s.entities[entityId]?.facts[predicate];
  if (!fact) return 'gm';
  const latest = fact.versions[fact.versions.length - 1];
  if (!latest) return 'gm';
  const raw = latest.author.toLowerCase();
  if (raw === 'llm') return 'llm';
  if (raw === 'player' || raw.startsWith('player.')) return 'player';
  if (raw === 'system') return 'system';
  return 'gm';
}

/**
 * Inline-error string for a row, or undefined.
 *
 * `key` is the caller-computed `errorKey(entityId, predicate)` —
 * pre-joining the two ids at the call site keeps this signature
 * single-arg-after-state, which the analyzer prefers (and the call
 * sites already compute the key for the `factDirty` lookup anyway).
 */
export function inlineErrorAtKey<S extends FactRowStateShape>(
  s: S,
  key: string,
): string | undefined {
  return s.inlineErrors[key];
}

/** Per-row dirty flag — the editor's #1 reactive signal. */
export function factDirtyAtKey<S extends FactRowStateShape>(
  s: S,
  key: string,
): boolean {
  return s.factDirty[key] === true;
}

/** LLM author mode — true while the row's ✨ suggest-prose call is in flight. */
export function aiDraftPendingAtKey<S extends FactRowStateShape>(s: S, key: string): boolean {
  return s.aiDraftPending[key] === true;
}

/**
 * LLM author mode — the row has no meaningful current value. The ✨
 * "Draft with AI" affordance is offered only on EMPTY prose fields: a
 * draft on a chain that already holds canon would shadow it (one chain
 * per subject+predicate), and rejecting would soft-delete the original.
 * Restricting to empty fields keeps Phase 1 lossless ("describe this").
 */
export function factIsEmptyAtPredicate<S extends FactRowStateShape>(
  s: S,
  entityId: string,
  predicate: string,
): boolean {
  const fact = s.entities[entityId]?.facts[predicate];
  if (!fact || fact.soft_deleted) return true;
  const latest = fact.versions[fact.versions.length - 1];
  if (!latest) return true;
  const v = latest.value;
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
}

/** LLM author mode — the row's last ✨ failure message (empty when none). */
export function aiDraftErrorAtKey<S extends FactRowStateShape>(s: S, key: string): string {
  return s.aiDraftError[key] ?? '';
}

/**
 * Resolved signed content URL for the asset id currently held by an
 * asset-kind fact at `(entity, predicate)`, or '' when there's no asset
 * value or its URL hasn't resolved yet. Bare-Identifier accessor so the
 * editor's reactive `<img>` src binding tracks both `s.entities` (the
 * value) and `s.assetUrls` (the resolved URL).
 */
export function assetUrlAtPredicate<S extends FactRowStateShape>(
  s: S,
  entityId: string,
  predicate: string,
): string {
  const fact = s.entities[entityId]?.facts[predicate];
  const latest = fact?.versions[fact.versions.length - 1];
  const v = latest?.value;
  if (typeof v !== 'string' || v === '') return '';
  return s.assetUrls[v] ?? '';
}

/** Per-row "saved at (epoch ms)" — drives the transient ✓ badge. */
export function factSavedAtKey<S extends FactRowStateShape>(
  s: S,
  key: string,
): number | undefined {
  return s.factSavedAt[key];
}

/** Per-row "commit in flight" — drives the transient "Saving…" cue. */
export function factSavingAtKey<S extends FactRowStateShape>(s: S, key: string): boolean {
  return s.factSaving[key] === true;
}

/** Currently-focused fact-input key (`errorKey(entityId, predicate)`). */
export function focusedFactKeyOf<S extends FactRowStateShape>(s: S): string | null {
  return s.focusedFactKey;
}

/** Hint-dismissed flag (localStorage-cached at boot). */
export function factHintDismissedOf<S extends FactRowStateShape>(s: S): boolean {
  return s.factHintDismissed;
}

/**
 * Controlled-input dirty buffer. Returns the buffered string the GM
 * has typed since the last successful save (or `undefined` if the
 * row hasn't been touched — the editor then falls back to the
 * persisted fact value). Function declaration shape so the llui
 * compiler's cross-file walker traces the `s.inputBuffer` path and
 * the value-binding's mask covers it.
 */
export function inputBufferAtKey<S extends FactRowStateShape>(
  s: S,
  key: string,
): string | undefined {
  return s.inputBuffer[key];
}
